import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as api from '../api';
import { useAuth } from '../auth';
import { euros, fmtDate, useT } from '../i18n';
import PageHeader from '../components/PageHeader';
import Notice from '../components/Notice';
import { TournamentForm } from './Tournaments';
import type { Match, TournamentDetail as Detail } from '../types';

export default function TournamentDetail() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const { t, lang } = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['tournament', id], queryFn: () => api.getTournament(id), refetchInterval: 20_000 });
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const isAdmin = user?.role === 'admin';

  const refresh = () => { qc.invalidateQueries({ queryKey: ['tournament', id] }); qc.invalidateQueries({ queryKey: ['tournaments'] }); qc.invalidateQueries({ queryKey: ['home'] }); qc.invalidateQueries({ queryKey: ['rankings'] }); };
  const mut = <A,>(fn: (a: A) => Promise<unknown>, after?: () => void) => useMutation({ mutationFn: fn, onSuccess: () => { setError(null); refresh(); after?.(); }, onError: (e: Error) => setError(e.message) });
  const join = mut(() => api.joinTournament(id));
  const leave = mut(() => api.leaveTournament(id));
  const add = mut((userId: string) => api.addEntry(id, userId));
  const remove = mut((userId: string) => api.removeEntry(id, userId));
  const start = mut(() => api.startTournament(id));
  const next = mut(() => api.nextRound(id));
  const finish = mut(() => api.finishTournament(id));
  const reopen = mut(() => api.reopenTournament(id));
  const del = mut(() => api.deleteTournament(id), () => nav('/tournaments'));
  const score = mut(({ m, a, b }: { m: string; a: number; b: number }) => api.enterScore(m, a, b));
  const clear = mut((m: string) => api.clearScore(m));
  const update = mut((input: Partial<import('../types').TournamentInput>) => api.updateTournament(id, input), () => setEditing(false));
  const setHc = mut(({ userId, handicap }: { userId: string; handicap: number }) => api.adminUpdateUser(userId, { handicap }));

  if (q.isLoading) return <div className="text-dim text-sm">{t('loading')}</div>;
  if (q.error || !q.data) return <div className="text-danger text-sm">{(q.error as Error)?.message}</div>;
  const d: Detail = q.data;
  const T = d.tournament;
  const name = (uid: string) => d.players.find((p) => p.id === uid)?.displayName ?? '?';
  const hc = (uid: string) => d.players.find((p) => p.id === uid)?.handicap ?? 0;
  const confirmed = d.entries.filter((e) => e.status === 'in');
  const waitlist = d.entries.filter((e) => e.status === 'waitlist');
  const rounds = [...new Set(d.matches.map((m) => m.round))].sort((a, b) => a - b);
  const lastRoundDone = rounds.length > 0 && d.matches.filter((m) => m.round === rounds[rounds.length - 1]).every((m) => m.scoreA != null);
  const canEnter = (m: Match) => T.status === 'live' && (isAdmin || [...m.a, ...m.b].includes(user!.id));

  return (
    <>
      <Link to="/tournaments" className="text-sm text-dim hover:text-chalk">← {t('back')}</Link>
      <PageHeader eyebrow={t(`t.status.${T.status}`)} title={T.name} subtitle={`${fmtDate(T.startsAt, lang)}${T.venue ? ` · ${T.venue}` : ''}`}
        action={T.status === 'open' ? (
          d.myStatus ? <button className="btn-secondary" onClick={() => leave.mutate(undefined)}>{t('t.leave')}</button>
            : <button className="btn-primary" onClick={() => join.mutate(undefined)}>{t('t.join')}</button>
        ) : undefined} />

      <div className="text-sm text-dim mb-4">
        {t('d.info', { format: t(`t.format.${T.format}`), courts: T.courts, rounds: T.rounds, games: T.gamesPerMatch, cost: (T.costPerGameCents / 100).toFixed(2).replace(/\.00$/, '') })}
        {' · '}{T.handicapEnabled ? t('d.hcinfo', { n: T.handicapMax }) : t('d.hcoff')}
        {T.notes && <div className="mt-1 whitespace-pre-line">{T.notes}</div>}
      </div>
      {error && <div className="mb-4"><Notice kind="error">{error}</Notice></div>}

      {isAdmin && (
        <div className="card p-4 mb-6 flex flex-wrap gap-2">
          {T.status === 'open' && <button className="btn-primary" disabled={confirmed.length < 4} title={confirmed.length < 4 ? t('d.min4') : ''} onClick={() => start.mutate(undefined)}>{t('d.start')}</button>}
          {T.status === 'live' && T.format === 'mexicano' && rounds.length < T.rounds && <button className="btn-primary" disabled={!lastRoundDone} onClick={() => next.mutate(undefined)}>{t('d.nextround')}</button>}
          {T.status === 'live' && <button className="btn-secondary" onClick={() => finish.mutate(undefined)}>{t('d.finish')}</button>}
          {T.status === 'live' && <button className="btn-ghost" onClick={() => { if (confirm(t('d.confirm.reopen'))) reopen.mutate(undefined); }}>{t('d.reopen.live')}</button>}
          {T.status === 'done' && <button className="btn-secondary" onClick={() => reopen.mutate(undefined)}>{t('d.reopen.done')}</button>}
          <button className="btn-ghost" onClick={() => setEditing((v) => !v)}>{t('t.edit')}</button>
          <button className="btn-danger ml-auto" onClick={() => { if (confirm(t('d.confirm.delete'))) del.mutate(undefined); }}>{t('delete')}</button>
        </div>
      )}
      {editing && (
        <div className="card p-5 mb-6">
          <TournamentForm initial={{ ...T, venue: T.venue ?? '', notes: T.notes ?? '' }} frozen={T.status !== 'open'} busy={update.isPending} error={update.error?.message}
            onSubmit={(i) => update.mutate(i)} onCancel={() => setEditing(false)} />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_1fr]">
        {/* Roster */}
        <section className="space-y-4">
          <div className="card">
            <div className="px-4 py-3 border-b border-ink-700 font-medium flex items-center justify-between">
              <span>{t('d.roster')}</span><span className="text-dim text-sm stat">{confirmed.length}/{T.maxPlayers}</span>
            </div>
            <ul className="divide-y divide-ink-700">
              {confirmed.map((e) => <RosterRow key={e.userId} uid={e.userId} name={name(e.userId)} hc={hc(e.userId)} isAdmin={isAdmin} open={T.status === 'open'} me={user!.id}
                onRemove={() => remove.mutate(e.userId)} onHc={(h) => setHc.mutate({ userId: e.userId, handicap: h })} />)}
            </ul>
            {waitlist.length > 0 && (
              <>
                <div className="px-4 py-2 border-y border-ink-700 text-xs uppercase tracking-wider text-dim">{t('d.waitlist')}</div>
                <ul className="divide-y divide-ink-700">
                  {waitlist.map((e, i) => <RosterRow key={e.userId} uid={e.userId} name={`${i + 1}. ${name(e.userId)}`} hc={hc(e.userId)} isAdmin={isAdmin} open={T.status === 'open'} me={user!.id}
                    onRemove={() => remove.mutate(e.userId)} onHc={(h) => setHc.mutate({ userId: e.userId, handicap: h })} />)}
                </ul>
              </>
            )}
            {isAdmin && T.status === 'open' && <AddPlayer exclude={d.entries.map((e) => e.userId)} onAdd={(uid) => add.mutate(uid)} />}
          </div>

          {d.standings.length > 0 && d.matches.length > 0 && (
            <div className="card">
              <div className="px-4 py-3 border-b border-ink-700 font-medium">{t('d.standings')}</div>
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wider text-dim"><th className="text-left px-4 py-2">#</th><th className="text-left py-2"></th><th className="text-right py-2">{t('d.pts')}</th><th className="text-right py-2">{t('d.wl')}</th><th className="text-right px-4 py-2">{t('d.balance')}</th></tr></thead>
                <tbody>
                  {d.standings.map((s, i) => (
                    <tr key={s.userId} className={`border-t border-ink-700/60 ${s.userId === user!.id ? 'bg-ball-500/5' : ''}`}>
                      <td className="px-4 py-2 stat text-dim">{i + 1}</td>
                      <td className="py-2 font-medium truncate max-w-[9rem]">{name(s.userId)}</td>
                      <td className="py-2 text-right stat font-semibold">{s.points}</td>
                      <td className="py-2 text-right stat text-dim">{s.gamesWon}–{s.gamesLost}</td>
                      <td className={`px-4 py-2 text-right stat ${s.cents > 0 ? 'text-ball-300' : s.cents < 0 ? 'text-danger' : 'text-dim'}`}>{s.balance > 0 ? '+' : ''}{s.balance} · {euros(s.cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 border-t border-ink-700">
                <div className="text-xs uppercase tracking-wider text-dim mb-2">{t('d.cash')}</div>
                {d.transfers.length ? (
                  <ul className="space-y-1 text-sm">{d.transfers.map((x, i) => <li key={i}>{t('d.pays', { from: name(x.from), to: name(x.to) })} <span className="stat font-semibold text-ball-300">{euros(x.cents)}</span></li>)}</ul>
                ) : <div className="text-sm text-dim">{t('d.square')}</div>}
              </div>
            </div>
          )}
        </section>

        {/* Schedule */}
        <section className="space-y-4">
          {!rounds.length && <div className="card p-6 text-dim text-sm">{t('d.nomatches')}</div>}
          {rounds.map((r) => {
            const ms = d.matches.filter((m) => m.round === r);
            const playing = new Set(ms.flatMap((m) => [...m.a, ...m.b]));
            const sitting = confirmed.map((e) => e.userId).filter((u) => !playing.has(u));
            return (
              <div key={r} className="card">
                <div className="px-4 py-3 border-b border-ink-700 flex items-center justify-between">
                  <span className="font-medium">{t('d.round', { n: r })}</span>
                  {sitting.length > 0 && <span className="text-xs text-dim">{t('d.sitting', { names: sitting.map(name).join(', ') })}</span>}
                </div>
                <ul className="divide-y divide-ink-700">
                  {ms.map((m) => <MatchCard key={m.id} m={m} name={name} games={T.gamesPerMatch} canEnter={canEnter(m)} isAdmin={isAdmin} me={user!.id}
                    busy={score.isPending} onScore={(a, b) => score.mutate({ m: m.id, a, b })} onClear={() => clear.mutate(m.id)} />)}
                </ul>
              </div>
            );
          })}
        </section>
      </div>
    </>
  );
}

function RosterRow({ uid, name, hc, isAdmin, open, me, onRemove, onHc }: { uid: string; name: string; hc: number; isAdmin: boolean; open: boolean; me: string; onRemove: () => void; onHc: (h: number) => void }) {
  const { t } = useT();
  return (
    <li className={`px-4 py-2 flex items-center gap-2 text-sm ${uid === me ? 'bg-ball-500/5' : ''}`}>
      <span className="flex-1 truncate font-medium">{name}</span>
      {isAdmin ? (
        <label className="flex items-center gap-1 text-xs text-dim">{t('d.hc')}
          <input type="number" min={-6} max={6} value={hc} onChange={(e) => onHc(Number(e.target.value))} className="w-14 py-1 px-2 text-center stat" />
        </label>
      ) : <span className="pill bg-ink-800 text-dim stat">{t('d.hc')} {hc > 0 ? `+${hc}` : hc}</span>}
      {isAdmin && open && <button className="btn-ghost text-xs py-1 px-2" onClick={onRemove}>{t('d.remove')}</button>}
    </li>
  );
}

function AddPlayer({ exclude, onAdd }: { exclude: string[]; onAdd: (uid: string) => void }) {
  const { t } = useT();
  const q = useQuery({ queryKey: ['players'], queryFn: api.getPlayers });
  const [sel, setSel] = useState('');
  const options = (q.data?.players ?? []).filter((p) => !exclude.includes(p.id));
  return (
    <div className="px-4 py-3 border-t border-ink-700 flex gap-2">
      <select value={sel} onChange={(e) => setSel(e.target.value)} className="text-sm py-1.5">
        <option value="">{t('d.addplayer')}…</option>
        {options.map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
      </select>
      <button className="btn-secondary text-sm py-1.5" disabled={!sel} onClick={() => { onAdd(sel); setSel(''); }}>+</button>
    </div>
  );
}

function MatchCard({ m, name, games, canEnter, isAdmin, me, busy, onScore, onClear }: {
  m: Match; name: (id: string) => string; games: number; canEnter: boolean; isAdmin: boolean; me: string; busy: boolean;
  onScore: (a: number, b: number) => void; onClear: () => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [a, setA] = useState<number>(m.scoreA ?? m.startA);
  const played = m.scoreA != null;
  const mine = m.a.includes(me) ? 'a' : m.b.includes(me) ? 'b' : null;
  const b = games - a;
  const valid = a >= m.startA && b >= m.startB && a <= games;
  const side = (ids: [string, string], s: 'a' | 'b') => (
    <div className={`flex-1 min-w-0 ${s === 'b' ? 'text-right' : ''}`}>
      <div className={`truncate ${mine === s ? 'font-semibold' : ''}`}>{name(ids[0])}</div>
      <div className={`truncate ${mine === s ? 'font-semibold' : ''}`}>{name(ids[1])}</div>
    </div>
  );
  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-3 text-sm">
        <span className="text-[11px] text-dim w-6 shrink-0" title={t('d.court', { n: m.court })}>C{m.court}</span>
        {side(m.a, 'a')}
        <button type="button" disabled={!canEnter} onClick={() => setOpen((v) => !v)}
          className={`stat text-lg font-semibold px-3 py-1 rounded-xl shrink-0 ${played ? 'bg-ink-800' : 'bg-ink-800 text-dim'} ${canEnter ? 'hover:ring-1 hover:ring-ball-500/40' : ''}`}>
          {played ? `${m.scoreA}–${m.scoreB}` : (m.startA || m.startB ? `${m.startA}–${m.startB}` : '–')}
        </button>
        {side(m.b, 'b')}
      </div>
      {!played && (m.startA > 0 || m.startB > 0) && <div className="text-[11px] text-court-400 mt-1 text-center">{t('d.headstart', { a: m.startA, b: m.startB })}</div>}
      {open && canEnter && (
        <div className="mt-3 flex flex-wrap items-center gap-2 justify-center">
          <input type="number" min={m.startA} max={games - m.startB} value={a} onChange={(e) => setA(Number(e.target.value))} className="w-16 text-center stat" />
          <span className="text-dim">–</span>
          <span className="w-16 text-center stat py-2.5">{b}</span>
          <button className="btn-primary text-sm py-1.5" disabled={!valid || busy} onClick={() => { onScore(a, b); setOpen(false); }}>{t('save')}</button>
          {played && isAdmin && <button className="btn-ghost text-sm py-1.5" onClick={() => { onClear(); setOpen(false); }}>{t('d.clear')}</button>}
          <div className="basis-full text-center text-[11px] text-dim">{t('d.score.hint', { n: games })}</div>
        </div>
      )}
    </li>
  );
}
